/** Eight certificate-depth examples per part for Vectors & Vector Spaces. */

export const LA_VECTORS_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Linear combination in $\\mathbb{R}^3$",
    setup: "Compute $3u-2v$ for $u=(2,-1,4)$ and $v=(0,3,-1)$.",
    steps: [
      { text: "Scale the first vector: $3u=(6,-3,12)$.", why: "Scalar multiplication acts on each component." },
      { text: "Scale the second vector: $2v=(0,6,-2)$.", why: "Same rule with factor $2$." },
      { text: "Subtraction is addition of the negative: $3u-2v=3u+(-2v)$.", why: "Keeps signs under control." },
      { text: "$-2v=(0,-6,2)$.", why: "Negate every component." },
      { text: "Add componentwise: first coordinate $6+0=6$.", why: "Addition is componentwise." },
      { text: "Second coordinate $-3+(-6)=-9$.", why: "Careful with two negatives." },
      { text: "Third coordinate $12+2=14$.", why: "Finish the sum." },
      { text: "So $3u-2v=(6,-9,14)$.", why: "Collect the three components." },
    ],
    result: "$3u-2v=(6,-9,14)$",
    check: "Reverse it: $(6,-9,14)+2v=(6,-3,12)=3u$.",
    mistake: "Multiplying only the first component by the scalar.",
    life: "Every mixing problem is a linear combination: $3$ units of recipe $u$ minus $2$ units of recipe $v$ is exactly how a plant computes net material flow per batch.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Norm and unit vector",
    setup: "Find $\\lVert v\\rVert$ and the unit vector in the direction of $v=(3,-4,12)$.",
    steps: [
      { text: "The norm is $\\lVert v\\rVert=\\sqrt{v_1^2+v_2^2+v_3^2}$.", why: "Pythagoras in $\\mathbb{R}^3$." },
      { text: "Square the components: $9$, $16$, $144$.", why: "Squares kill the signs." },
      { text: "Sum them: $9+16+144=169$.", why: "Add before taking the root." },
      { text: "$\\lVert v\\rVert=\\sqrt{169}=13$.", why: "A perfect square, so no decimals needed." },
      { text: "A unit vector is $\\hat{v}=v/\\lVert v\\rVert$.", why: "Dividing by the length rescales to length $1$." },
      { text: "$\\hat{v}=\\left(\\tfrac{3}{13},-\\tfrac{4}{13},\\tfrac{12}{13}\\right)$.", why: "Divide each component by $13$." },
      { text: "Verify: $\\left(\\tfrac{3}{13}\\right)^2+\\left(\\tfrac{4}{13}\\right)^2+\\left(\\tfrac{12}{13}\\right)^2=\\tfrac{169}{169}=1$.", why: "Unit length confirmed." },
      { text: "Direction is unchanged because $1/13>0$.", why: "Positive scalars preserve direction." },
    ],
    result: "$\\lVert v\\rVert=13$ and $\\hat{v}=\\left(\\tfrac{3}{13},-\\tfrac{4}{13},\\tfrac{12}{13}\\right)$",
    check: "$13\\hat{v}=v$.",
    mistake: "Writing $\\lVert v\\rVert=3-4+12$; the norm is not a sum of components.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Angle between two vectors",
    setup: "Find the angle between $u=(1,2,2)$ and $v=(2,0,-1)$.",
    steps: [
      { text: "Dot product: $u\\cdot v=1(2)+2(0)+2(-1)$.", why: "Multiply matching components and add." },
      { text: "So $u\\cdot v=2+0-2=0$.", why: "The terms cancel exactly." },
      { text: "Lengths: $\\lVert u\\rVert=\\sqrt{1+4+4}=3$.", why: "Needed for the cosine formula." },
      { text: "$\\lVert v\\rVert=\\sqrt{4+0+1}=\\sqrt{5}$.", why: "Leave it in radical form." },
      { text: "Formula: $\\cos\\theta=\\dfrac{u\\cdot v}{\\lVert u\\rVert\\lVert v\\rVert}$.", why: "Definition of the angle." },
      { text: "$\\cos\\theta=\\dfrac{0}{3\\sqrt{5}}=0$.", why: "Zero numerator, nonzero denominator." },
      { text: "$\\theta=\\arccos 0=90^\\circ$.", why: "Cosine vanishes only at a right angle on $[0,180^\\circ]$." },
      { text: "Conclusion: $u\\perp v$.", why: "Zero dot product means orthogonal." },
    ],
    result: "$\\theta=90^\\circ$ — the vectors are orthogonal",
    check: "Orthogonality also gives $\\lVert u+v\\rVert^2=\\lVert u\\rVert^2+\\lVert v\\rVert^2=9+5=14$.",
    mistake: "Concluding \"parallel\" from a zero dot product; zero means perpendicular.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Projection of one vector onto another",
    setup: "Project $b=(4,3)$ onto $a=(2,1)$ and split $b$ into parallel and perpendicular parts.",
    steps: [
      { text: "$a\\cdot b=2(4)+1(3)=11$.", why: "Numerator of the projection." },
      { text: "$a\\cdot a=4+1=5$.", why: "Denominator is $\\lVert a\\rVert^2$." },
      { text: "$\\operatorname{proj}_a b=\\dfrac{a\\cdot b}{a\\cdot a}\\,a$.", why: "Projection formula." },
      { text: "Scalar factor is $\\tfrac{11}{5}$.", why: "Keep it as a fraction." },
      { text: "$\\operatorname{proj}_a b=\\tfrac{11}{5}(2,1)=\\left(\\tfrac{22}{5},\\tfrac{11}{5}\\right)$.", why: "Scale $a$." },
      { text: "Perpendicular part: $b-\\operatorname{proj}_a b=\\left(4-\\tfrac{22}{5},\\,3-\\tfrac{11}{5}\\right)$.", why: "Whatever is left over is orthogonal to $a$." },
      { text: "$=\\left(-\\tfrac{2}{5},\\tfrac{4}{5}\\right)$.", why: "Common denominator $5$." },
      { text: "Check orthogonality: $(2,1)\\cdot\\left(-\\tfrac{2}{5},\\tfrac{4}{5}\\right)=-\\tfrac{4}{5}+\\tfrac{4}{5}=0$.", why: "Confirms the split." },
    ],
    result: "$\\operatorname{proj}_a b=\\left(\\tfrac{22}{5},\\tfrac{11}{5}\\right)$, residual $\\left(-\\tfrac{2}{5},\\tfrac{4}{5}\\right)$",
    check: "The two pieces add back to $b=(4,3)$.",
    mistake: "Dividing by $\\lVert a\\rVert$ instead of $\\lVert a\\rVert^2$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Cross product and triangle area",
    setup: "Find the area of the triangle with vertices $P(1,0,0)$, $Q(0,2,0)$, $R(0,0,3)$.",
    steps: [
      { text: "Edge vectors: $\\vec{PQ}=Q-P=(-1,2,0)$.", why: "Anchor both edges at $P$." },
      { text: "$\\vec{PR}=R-P=(-1,0,3)$.", why: "Second edge from the same vertex." },
      { text: "Cross product first component: $2(3)-0(0)=6$.", why: "$(u_2v_3-u_3v_2)$." },
      { text: "Second component: $0(-1)-(-1)(3)=3$.", why: "$(u_3v_1-u_1v_3)$; watch the sign pattern." },
      { text: "Third component: $(-1)(0)-2(-1)=2$.", why: "$(u_1v_2-u_2v_1)$." },
      { text: "So $\\vec{PQ}\\times\\vec{PR}=(6,3,2)$.", why: "This vector is normal to the triangle's plane." },
      { text: "Its length: $\\sqrt{36+9+4}=\\sqrt{49}=7$.", why: "The parallelogram area." },
      { text: "Triangle area $=\\tfrac12(7)=\\tfrac72$.", why: "A triangle is half a parallelogram." },
    ],
    result: "Area $=\\tfrac72=3.5$",
    check: "$(6,3,2)$ is orthogonal to both edges: $(6,3,2)\\cdot(-1,2,0)=0$.",
    mistake: "Forgetting the factor $\\tfrac12$ and reporting the parallelogram area.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Cauchy–Schwarz and the triangle inequality",
    setup: "For $u=(1,1,1)$ and $v=(2,-1,3)$, verify $|u\\cdot v|\\le\\lVert u\\rVert\\lVert v\\rVert$ and $\\lVert u+v\\rVert\\le\\lVert u\\rVert+\\lVert v\\rVert$.",
    steps: [
      { text: "$u\\cdot v=2-1+3=4$, so $|u\\cdot v|=4$.", why: "Left side of Cauchy–Schwarz." },
      { text: "$\\lVert u\\rVert=\\sqrt{3}\\approx1.732$.", why: "All components are $1$." },
      { text: "$\\lVert v\\rVert=\\sqrt{4+1+9}=\\sqrt{14}\\approx3.742$.", why: "Second length." },
      { text: "Product $\\sqrt{3}\\sqrt{14}=\\sqrt{42}\\approx6.481$.", why: "Right side of Cauchy–Schwarz." },
      { text: "$4\\le 6.481$ holds, with strict inequality, so $u$ and $v$ are not parallel.", why: "Equality happens only for parallel vectors." },
      { text: "$u+v=(3,0,4)$, so $\\lVert u+v\\rVert=5$.", why: "A $3$–$4$–$5$ length." },
      { text: "$\\lVert u\\rVert+\\lVert v\\rVert\\approx1.732+3.742=5.474$.", why: "Right side of the triangle inequality." },
      { text: "$5\\le 5.474$ holds.", why: "Both inequalities verified numerically." },
    ],
    result: "Both inequalities hold strictly: $4<\\sqrt{42}$ and $5<5.474$",
    check: "$\\cos\\theta=4/\\sqrt{42}\\approx0.617$, a genuine angle of about $51.9^\\circ$.",
    mistake: "Assuming $\\lVert u+v\\rVert=\\lVert u\\rVert+\\lVert v\\rVert$; that only happens for same-direction vectors.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Distance from a point to a line",
    setup: "Find the distance from $P(3,4,1)$ to the line through $A(1,0,0)$ with direction $d=(0,1,1)$.",
    steps: [
      { text: "Vector from the anchor: $w=P-A=(2,4,1)$.", why: "Set up a triangle with the direction vector." },
      { text: "$d\\cdot w=0(2)+1(4)+1(1)=5$.", why: "Needed for the projection along the line." },
      { text: "$d\\cdot d=0+1+1=2$.", why: "Squared length of the direction." },
      { text: "$\\operatorname{proj}_d w=\\tfrac{5}{2}(0,1,1)=\\left(0,\\tfrac52,\\tfrac52\\right)$.", why: "Component of $w$ along the line." },
      { text: "Perpendicular part: $w-\\operatorname{proj}_d w=\\left(2,\\tfrac32,-\\tfrac32\\right)$.", why: "This is the shortest connector." },
      { text: "Its squared length: $4+\\tfrac94+\\tfrac94=\\tfrac{17}{2}$.", why: "Add the squares." },
      { text: "Distance $=\\sqrt{17/2}=\\dfrac{\\sqrt{34}}{2}\\approx2.915$.", why: "Rationalize the radical." },
      { text: "The foot of the perpendicular is $A+\\operatorname{proj}_d w=\\left(1,\\tfrac52,\\tfrac52\\right)$.", why: "Closest point on the line." },
    ],
    result: "Distance $=\\dfrac{\\sqrt{34}}{2}\\approx2.92$",
    check: "$\\left(2,\\tfrac32,-\\tfrac32\\right)\\cdot(0,1,1)=0$, so the connector really is perpendicular.",
    mistake: "Using $\\lVert P-A\\rVert$ as the distance; that is the slanted distance, not the perpendicular one.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Work as a dot product",
    setup: "A force $F=(6,-2,3)$ N moves an object along the displacement $s=(2,5,4)$ m. Find the work and the angle.",
    steps: [
      { text: "Work is $W=F\\cdot s$.", why: "Only the component of force along the motion does work." },
      { text: "$F\\cdot s=6(2)+(-2)(5)+3(4)$.", why: "Componentwise products." },
      { text: "$=12-10+12=14$.", why: "Careful with the negative middle term." },
      { text: "So $W=14$ joules.", why: "Newton-metres are joules." },
      { text: "$\\lVert F\\rVert=\\sqrt{36+4+9}=7$.", why: "Force magnitude." },
      { text: "$\\lVert s\\rVert=\\sqrt{4+25+16}=\\sqrt{45}=3\\sqrt5$.", why: "Displacement magnitude." },
      { text: "$\\cos\\theta=\\dfrac{14}{7\\cdot3\\sqrt5}=\\dfrac{2}{3\\sqrt5}\\approx0.298$.", why: "Angle from the same dot product." },
      { text: "$\\theta\\approx72.7^\\circ$, and since $\\theta<90^\\circ$ the work is positive.", why: "Sign of the work matches the sign of the cosine." },
    ],
    result: "$W=14$ J at an angle of about $72.7^\\circ$",
    check: "The force component along $s$ is $W/\\lVert s\\rVert=14/(3\\sqrt5)\\approx2.09$ N.",
    mistake: "Multiplying magnitudes only ($7\\times3\\sqrt5$) and forgetting the $\\cos\\theta$ factor.",
  },
];

export const LA_VECTORS_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Is a vector in a span?",
    setup: "Decide whether $b=(5,7,12)$ lies in $\\mathrm{Span}\\{(1,2,3),(2,1,3)\\}$.",
    steps: [
      { text: "Set up $c_1(1,2,3)+c_2(2,1,3)=(5,7,12)$.", why: "Membership in a span is a solvability question." },
      { text: "Component equations: $c_1+2c_2=5$, $2c_1+c_2=7$, $3c_1+3c_2=12$.", why: "One equation per coordinate." },
      { text: "From the third equation, $c_1+c_2=4$.", why: "Divide by $3$ to simplify." },
      { text: "Subtract this from the first: $c_2=1$.", why: "Eliminate $c_1$." },
      { text: "Then $c_1=3$.", why: "Back-substitute into $c_1+c_2=4$." },
      { text: "Test in the second equation: $2(3)+1=7$. True.", why: "All three equations must hold." },
      { text: "So $b=3(1,2,3)+1(2,1,3)$.", why: "Explicit combination found." },
      { text: "Hence $b$ is in the span; the system was consistent.", why: "Consistency is the whole test." },
    ],
    result: "Yes: $b=3v_1+v_2$",
    check: "$3(1,2,3)=(3,6,9)$; adding $(2,1,3)$ gives $(5,7,12)$.",
    mistake: "Stopping after two equations without checking the third coordinate.",
    life: "A colour on a screen is in the span of the RGB primaries exactly when the mixing system is consistent — printers solve this every time they match a brand colour.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Independence of three vectors in $\\mathbb{R}^3$",
    setup: "Are $v_1=(1,0,1)$, $v_2=(1,1,0)$, $v_3=(0,1,-1)$ linearly independent?",
    steps: [
      { text: "Independence means $c_1v_1+c_2v_2+c_3v_3=0$ forces all $c_i=0$.", why: "Definition." },
      { text: "Put the vectors as columns of $A=\\begin{pmatrix}1&1&0\\\\0&1&1\\\\1&0&-1\\end{pmatrix}$.", why: "Matrix form of the same question." },
      { text: "Expand the determinant along the first row: $1(1\\cdot(-1)-1\\cdot0)-1(0\\cdot(-1)-1\\cdot1)+0$.", why: "$3\\times3$ cofactor expansion." },
      { text: "$=1(-1)-1(-1)=-1+1=0$.", why: "Determinant is zero." },
      { text: "A zero determinant means a nontrivial null vector exists.", why: "Singular matrix." },
      { text: "Find it: note $v_3=v_2-v_1$ since $(1,1,0)-(1,0,1)=(0,1,-1)$.", why: "An explicit dependence relation." },
      { text: "So $v_1-v_2+v_3=0$ with coefficients $(1,-1,1)\\ne 0$.", why: "Nontrivial combination gives zero." },
      { text: "Therefore the set is dependent, and its span has dimension $2$.", why: "Two pivots, one redundant column." },
    ],
    result: "Dependent: $v_1-v_2+v_3=0$, so $\\dim\\mathrm{Span}=2$",
    check: "$(1,0,1)-(1,1,0)+(0,1,-1)=(0,0,0)$.",
    mistake: "Concluding independence just because no vector is a scalar multiple of another.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Basis for the span of four vectors",
    setup: "Find a basis for $\\mathrm{Span}\\{(1,2,1),(2,4,2),(0,1,1),(1,3,2)\\}$ and state its dimension.",
    steps: [
      { text: "Place the vectors as columns of a $3\\times4$ matrix $A$.", why: "Pivot columns of $A$ form a basis of the column space." },
      { text: "Note column $2$ is $2\\times$ column $1$, so it will never be a pivot column.", why: "Obvious multiple." },
      { text: "Row-reduce: $R_2-2R_1$ and $R_3-R_1$ clear the first column.", why: "Standard elimination." },
      { text: "The first column gives a pivot in row $1$.", why: "Leading entry $1$." },
      { text: "Column $3=(0,1,1)$ is not a multiple of column $1$, so it supplies the second pivot.", why: "New direction." },
      { text: "Column $4$: check $(1,3,2)=(1,2,1)+(0,1,1)$.", why: "Test against the pivots already found." },
      { text: "That works, so column $4$ is dependent and gives no new pivot.", why: "Two pivots total." },
      { text: "Basis $\\{(1,2,1),(0,1,1)\\}$, dimension $2$; the span is a plane through the origin.", why: "Rank equals number of pivots." },
    ],
    result: "Basis $\\{(1,2,1),(0,1,1)\\}$, $\\dim=2$",
    check: "All four original vectors satisfy $x-y+z=0$: e.g. $1-3+2=0$.",
    mistake: "Reporting the row-reduced columns as the basis; use the original pivot columns.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Coordinates in a nonstandard basis",
    setup: "With $B=\\{(1,1,0),(0,1,1),(1,0,1)\\}$, find $[x]_B$ for $x=(2,3,1)$.",
    steps: [
      { text: "Solve $c_1(1,1,0)+c_2(0,1,1)+c_3(1,0,1)=(2,3,1)$.", why: "Coordinates are the unique weights." },
      { text: "Equations: $c_1+c_3=2$, $c_1+c_2=3$, $c_2+c_3=1$.", why: "One per coordinate." },
      { text: "Add all three: $2(c_1+c_2+c_3)=6$, so $c_1+c_2+c_3=3$.", why: "Symmetric shortcut." },
      { text: "Subtract $c_2+c_3=1$: $c_1=2$.", why: "Isolate one unknown." },
      { text: "Then $c_3=2-c_1=0$.", why: "First equation." },
      { text: "And $c_2=3-c_1=1$.", why: "Second equation." },
      { text: "Check the third: $c_2+c_3=1+0=1$. True.", why: "Consistency check." },
      { text: "So $[x]_B=(2,1,0)$.", why: "Uniqueness follows because $B$ is a basis." },
    ],
    result: "$[x]_B=(2,1,0)$",
    check: "$2(1,1,0)+1(0,1,1)=(2,3,1)=x$.",
    mistake: "Reading coordinates off in the wrong basis order; $[x]_B$ depends on how $B$ is listed.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Is a subset a subspace?",
    setup: "Decide whether $W=\\{(x,y,z):x+2y-z=0\\}$ and $V=\\{(x,y,z):x+2y-z=4\\}$ are subspaces of $\\mathbb{R}^3$.",
    steps: [
      { text: "A subspace must contain $0$ and be closed under addition and scaling.", why: "Three-part test." },
      { text: "For $W$: $0+0-0=0$, so $(0,0,0)\\in W$.", why: "Zero vector present." },
      { text: "Take $u,v\\in W$. Then $(u_1+v_1)+2(u_2+v_2)-(u_3+v_3)=0+0=0$.", why: "Closed under addition since the constraint is linear and homogeneous." },
      { text: "And $cu$ gives $c(u_1+2u_2-u_3)=c\\cdot0=0$.", why: "Closed under scaling." },
      { text: "So $W$ is a subspace; solving $x=z-2y$ gives basis $\\{(-2,1,0),(1,0,1)\\}$, $\\dim W=2$.", why: "Free variables $y,z$." },
      { text: "For $V$: test the zero vector: $0+0-0=0\\ne 4$.", why: "First checkpoint fails." },
      { text: "So $V$ misses the origin and cannot be a subspace.", why: "One failed axiom is enough." },
      { text: "Geometrically $V$ is a plane parallel to $W$, shifted off the origin — an affine set.", why: "Names the structure precisely." },
    ],
    result: "$W$ is a $2$-dimensional subspace; $V$ is not a subspace (no zero vector)",
    check: "$(-2,1,0)$ and $(1,0,1)$ both satisfy $x+2y-z=0$ and are independent.",
    mistake: "Calling every plane a subspace; only planes through the origin qualify.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Extend an independent set to a basis of $\\mathbb{R}^3$",
    setup: "Extend $\\{(1,1,1),(1,2,3)\\}$ to a basis of $\\mathbb{R}^3$.",
    steps: [
      { text: "The two vectors are independent (not multiples), so they span a plane.", why: "Starting point." },
      { text: "Find the plane's normal: $(1,1,1)\\times(1,2,3)$.", why: "Anything off the plane completes the basis." },
      { text: "First component: $1(3)-1(2)=1$.", why: "Cross-product pattern." },
      { text: "Second: $1(1)-1(3)=-2$.", why: "Middle sign flip already applied." },
      { text: "Third: $1(2)-1(1)=1$.", why: "Third component." },
      { text: "Normal $n=(1,-2,1)$, so the plane is $x-2y+z=0$.", why: "Plane equation from the normal." },
      { text: "Pick any vector with $x-2y+z\\ne0$, e.g. $e_1=(1,0,0)$ giving $1\\ne0$.", why: "Off-plane guarantees independence." },
      { text: "Then $\\{(1,1,1),(1,2,3),(1,0,0)\\}$ is independent and has $3$ elements, so it is a basis of $\\mathbb{R}^3$.", why: "Three independent vectors in $\\mathbb{R}^3$ always span." },
    ],
    result: "Basis $\\{(1,1,1),(1,2,3),(1,0,0)\\}$",
    check: "$\\det\\begin{pmatrix}1&1&1\\\\1&2&0\\\\1&3&0\\end{pmatrix}=-1\\ne0$.",
    mistake: "Adding a vector that already lies in the plane, e.g. $(2,3,4)=(1,1,1)+(1,2,3)$.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Gram–Schmidt on two vectors",
    setup: "Build an orthogonal basis for $\\mathrm{Span}\\{(1,1,0),(1,0,1)\\}$.",
    steps: [
      { text: "Keep $w_1=v_1=(1,1,0)$.", why: "Gram–Schmidt starts with the first vector unchanged." },
      { text: "$w_1\\cdot v_2=1(1)+1(0)+0(1)=1$.", why: "Projection numerator." },
      { text: "$w_1\\cdot w_1=2$.", why: "Projection denominator." },
      { text: "$\\operatorname{proj}_{w_1}v_2=\\tfrac12(1,1,0)=\\left(\\tfrac12,\\tfrac12,0\\right)$.", why: "Remove the overlap." },
      { text: "$w_2=v_2-\\operatorname{proj}_{w_1}v_2=\\left(\\tfrac12,-\\tfrac12,1\\right)$.", why: "Orthogonal remainder." },
      { text: "Scale by $2$ for tidiness: $w_2=(1,-1,2)$.", why: "Scaling preserves orthogonality and the span." },
      { text: "Check: $(1,1,0)\\cdot(1,-1,2)=1-1+0=0$.", why: "Orthogonality confirmed." },
      { text: "Normalize if needed: $\\hat{w}_1=\\tfrac{1}{\\sqrt2}(1,1,0)$, $\\hat{w}_2=\\tfrac{1}{\\sqrt6}(1,-1,2)$.", why: "Orthonormal version." },
    ],
    result: "Orthogonal basis $\\{(1,1,0),(1,-1,2)\\}$ of the same plane",
    check: "$v_2=(1,0,1)=\\tfrac12(1,1,0)+\\tfrac12(1,-1,2)$, so the span is unchanged.",
    mistake: "Subtracting $v_1$ itself instead of the projection of $v_2$ onto $v_1$.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Rank–nullity on a $3\\times4$ matrix",
    setup: "For $A=\\begin{pmatrix}1&2&0&1\\\\0&0&1&2\\\\1&2&1&3\\end{pmatrix}$, find rank, nullity, and a basis of the nullspace.",
    steps: [
      { text: "Row-reduce: $R_3-R_1$ gives $(0,0,1,2)$.", why: "Clear the first column." },
      { text: "Then $R_3-R_2=(0,0,0,0)$.", why: "Row $3$ was redundant." },
      { text: "Echelon form has pivots in columns $1$ and $3$, so $\\mathrm{rank}(A)=2$.", why: "Count pivots." },
      { text: "Free variables are $x_2$ and $x_4$, so nullity $=4-2=2$.", why: "Rank–nullity: $r+\\text{nullity}=n$." },
      { text: "Pivot equations: $x_1+2x_2+x_4=0$ and $x_3+2x_4=0$.", why: "Read off the reduced rows." },
      { text: "Set $x_2=1,x_4=0$: $x_3=0$, $x_1=-2$, giving $(-2,1,0,0)$.", why: "First null basis vector." },
      { text: "Set $x_2=0,x_4=1$: $x_3=-2$, $x_1=-1$, giving $(-1,0,-2,1)$.", why: "Second null basis vector." },
      { text: "Nullspace basis $\\{(-2,1,0,0),(-1,0,-2,1)\\}$; column space is $2$-dimensional.", why: "Rank–nullity is fully accounted for." },
    ],
    result: "$\\mathrm{rank}=2$, nullity $=2$, nullspace basis $\\{(-2,1,0,0),(-1,0,-2,1)\\}$",
    check: "$A(-1,0,-2,1)^{T}=(-1+0+0+1,\\,-2+2,\\,-1+0-2+3)=(0,0,0)$.",
    mistake: "Using the number of rows instead of the number of columns in $r+\\text{nullity}=n$.",
  },
];
